import { HttpErrorResponse } from "@angular/common/http";
import { Component, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { AccessControlService } from "app/access-control/access-control-service";
import { AuthService } from "app/auth/auth.service";
import {
  AlertController,
  Events,
  LoadingController,
  ModalController,
  ModalOptions,
  NavController,
  NavParams,
} from "ionic-angular";
import { commonVar } from "providers/common-var/common";
import { FacilitiesService } from "providers/facilities-service/facilities.service";
import { NavBarServiceProvider } from "providers/nav-bar-service/nav-bar-service";
import { ResourceManagementService } from "providers/resource-management/resource-management.service";
import { StateManagementService } from "providers/state-management/state-management.service";
import { UsersService } from "providers/users-service/users.service";
import { Subscription } from "rxjs";
import { GeneralFacility } from "../../../../components/facilities/general-facility/general-facility";
import { RatesFacility } from "../../../../components/facilities/rates-facility/rates-facility";
import {
  FacilityDetail,
  FacilityRate,
} from "../../../../models/facilities/facility-details";
import { UtilityService } from "../../../../providers/utility-service/utility-service";

@Component({
  selector: "create-facility",
  templateUrl: "create-facility.html",
})
export class CreateFacilityPage implements OnInit {
  @ViewChild(GeneralFacility) generalFacility: GeneralFacility;
  @ViewChild(RatesFacility) ratesFacility: RatesFacility;

  parentForm: FormGroup;
  touched: boolean;
  contactRemoved: boolean;
  override: boolean;
  facilityData: any;
  _saveEnabled: boolean = false;
  subscriptions: Subscription[] = [];

  _isEditMode: boolean = true;

  tabs = [
    {
      id: "generalDetails",
      title: "General",
      selected: true,
      icon: "md-radio-button-off",
      visible: true,
    },
    {
      id: "rates",
      title: "Rates",
      selected: false,
      icon: "md-radio-button-off",
      visible: false,
    },
  ];
  activeTab = this.tabs[0];
  activeTabId = this.activeTab.id;

  cardHeader: string = "Facilities";
  emailRegex: RegExp = commonVar.EMAIL_REGEX;

  facilityRates: FacilityRate[] = [];

  // CO-2419: default-OFF. Gates the CO-3228 generalisation of the Rates tab rule
  // (see setRatesTabVisibility) so the whole epic switches as one unit.
  q5001Enabled: boolean = false;

  returnToGrid: boolean = false;

  get saveEnabled() {
    return this._saveEnabled;
  }

  set saveEnabled(value: boolean) {
    this._saveEnabled = value;
  }

  get isEditMode() {
    return this._isEditMode;
  }

  get currentFacility() {
    return this.facilityData;
  }

  constructor(
    protected accessControlService: AccessControlService,
    protected authSvc: AuthService,
    protected navBarSvc: NavBarServiceProvider,
    protected navCtrl: NavController,
    protected navParams: NavParams,
    protected modalController: ModalController,
    protected resourceManagementService: ResourceManagementService,
    protected loader: LoadingController,
    protected userService: UsersService,
    protected stateManagementService: StateManagementService,
    protected fb: FormBuilder,
    protected alertController: AlertController,
    protected events: Events,
    private utilityService: UtilityService,
    private facilityService: FacilitiesService,
    public modalCtrl: ModalController
  ) {
    this.facilityData = navParams.get("facilityData");
  }

  ngOnInit() {
    // CO-3228: re-evaluate on arrival rather than only reading once — the toggle
    // is a BehaviorSubject that may still be null on the first emission.
    this.subscriptions.push(
      this.stateManagementService.q5001HomeResidenceToggle.subscribe((x) => {
        this.q5001Enabled = x != null ? x : this.q5001Enabled;
        this.setRatesTabVisibility();
      })
    );
    if (this.facilityData && this.facilityData.facilityId) {
      this.tabs[0].icon = "md-checkmark";
      this.getAllFacilityRates();
    }
    this.touched = false;
    this.override = false;
    this.contactRemoved = false;
    this.setRatesTabVisibility();
  }

  ionViewCanLeave(): boolean {
    if (
      this.isEditMode &&
      !this.override &&
      (this.contactRemoved || this.touched)
    ) {
      this.cancel();
      return false;
    } else {
      return true;
    }
  }

  ionViewWillEnter() {}

  ionViewCanEnter() {
    return this.authSvc.authenticated();
  }

  ionViewDidLoad() {
    this.stateManagementService.setAssessmentType("Company / Locations");
  }

  close() {
    if (this.navParams.data.parent) {
      this.navParams.data.parent.getFacilities();
    }
    this.navCtrl.pop();
  }

  save(event: FacilityDetail) {
    this.create(event);
  }

  setTouched(touched: boolean) {
    this.touched = touched;
  }

  setContactRemoved(removed: boolean) {
    this.contactRemoved = removed;
  }

  private create(obj) {
    this.saveEnabled = false;
    if (obj.facilityId && obj.facilityId > 0) {
      this.returnToGrid = true;
      this.showSaveFacilityModal(obj);
    } else {
      this.saveFacilityDetails(obj, "Creating Facility..");
    }
  }

  showSaveFacilityModal(facility) {
    let modalPage = this.modalCtrl.create(
      "SaveFacilityPage",
      {
        title: "Facility Information Change",
        commentPlaceholderKey: "archiveFacilityCommentPlaceHolder",
        popupMessageKey: "saveFacilityWarningMessage",
        section: "facility",
      },
      {
        enableBackdropDismiss: false,
        cssClass: "save-facility-confirmation-popup",
      }
    );
    modalPage.onDidDismiss((data) => {
      if (data.save) {
        facility.comment = data.comment;
        facility.status = this.facilityData.status;
        facility.effectiveDate = data.effectiveDate;
        this.saveFacilityDetails(facility, "Saving Facility..");
      }
    });
    modalPage.present();
  }

  saveFacilityDetails(facility, spinnerMessage) {
    this.utilityService.increaseSpinnerCount(spinnerMessage);
    facility.status = "Active";
    this.facilityService
      .saveFacilityDetails(facility)
      .pipe()
      .take(1)
      .subscribe((data) => {
        this.onSaveSuccess(data);
      }, this.onSaveError);
  }

  private onSaveSuccess = (data: any) => {
    setTimeout(() => {
      this.utilityService.decreaseSpinnerCount();
      this.saveEnabled = false;
      this.facilityData = data;
      this.tabs[0].icon = "md-checkmark";
      this.setRatesTabVisibility();
      this.touched = false;
      this.override = false;
      this.contactRemoved = false;
      if (this.returnToGrid) {
        if (this.navParams.data.parent) {
          this.navParams.data.parent.getFacilities();
        }
        this.navCtrl.pop();
      } else {
        this.onSaveSuccessAlert();
      }
    }, 2000);
  };

  private onSaveSuccessAlert = () => {
    this.showAlert({
      title: "Success",
      message: "Facility Saved",
    });
  };

  private onSaveError = (error) => {
    this.saveEnabled = true;
    this.utilityService.decreaseSpinnerCount();
    if (error instanceof HttpErrorResponse) {
      if (error.error) {
        this.showAlert({
          title: "Error",
          message: error.error.message,
        });
      } else {
        this.showAlert({
          title: "Error",
          message: error.message,
        });
      }
    }
  };

  selectTab(selectedTab) {
    // update tabs
    this.tabs = this.tabs.map((tab) => {
      tab.selected = tab.id === selectedTab.id;
      return tab;
    });

    // update activeTab key for sections displayed
    this.activeTab = selectedTab;
    this.activeTabId = selectedTab.id;
  }

  /**
   * CO-3228 / REQ-006, REQ-008 — Rates tab visibility follows the facility's
   * "Billable Room and Board" flag, not only its facility type.
   *
   * R&B pays the facility a per-diem and the per-diem is entered on this tab. Once
   * REQ-006 makes R&B an explicit per-facility choice, a type-only rule leaves an
   * operator able to mark a Q5001 facility R&B-billable with nowhere to record its
   * rate.
   *
   * The rule is a UNION, not a replacement. The type limb has to stay: every
   * existing non-Q5009 facility has isEligibleForRNB=false stored, because
   * general-facility's mapFacilityForm() forced it false before CO-3228, so
   * keying visibility purely on the flag would hide the Rates tab on every
   * existing Q5003/Q5004/Q5010 facility until the REQ-014 migration backfilled it.
   * As written, nothing visible today becomes hidden.
   *
   * Note Q5009 is deliberately absent from the type limb — it has always required
   * the flag, and the flag limb covers it.
   */
  static readonly RATES_BY_FACILITY_TYPE = [
    "NURSING_HOME", // Q5003
    "SKILLED_NURSING_FACILITY", // Q5004
    "HOSPICE_RESIDENTIAL_FACILITY", // Q5010
  ];

  setRatesTabVisibility() {
    const facility = this.facilityData;
    if (!facility || !facility.facilityId) {
      this.tabs[1].visible = false;
      return;
    }

    const ratesFromType =
      CreateFacilityPage.RATES_BY_FACILITY_TYPE.indexOf(
        facility.facilityType
      ) !== -1;

    if (this.q5001Enabled) {
      this.tabs[1].visible = ratesFromType || !!facility.isEligibleForRNB;
    } else {
      this.tabs[1].visible =
        ratesFromType ||
        (facility.facilityType == "UNSPECIFIED" && !!facility.isEligibleForRNB);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  reviewTabs() {
    this.setRatesTabVisibility();
  }

  cancel() {
    this.presentEditViewPopover();
  }

  presentEditViewPopover() {
    const deleteModalOptions: ModalOptions = {
      enableBackdropDismiss: false,
      cssClass: "delete-facility-confirmation-popup",
    };

    let modalPage = this.modalCtrl.create(
      "CancelFacilityPage",
      {
        title: "Discard Changes",
      },
      deleteModalOptions
    );
    modalPage.onDidDismiss((data) => {
      if (data.cancel) {
        this.override = true;
        if (this.navParams.data.parent) {
          this.navParams.data.parent.getFacilities();
        }
        this.navCtrl.pop();
      }
    });
    modalPage.present();
  }

  showAlert = ({ title, message }) => {
    let alert = this.alertController.create({
      title,
      message,
      buttons: [
        {
          text: "Ok",
        },
      ],
    });
    alert.present();
  };

  goBack() {
    if (this.navParams.data.parent) {
      this.navParams.data.parent.getFacilities();
    }
    this.navCtrl.pop();
  }

  getAllFacilityRates() {
    this.facilityService
      .getFacilityRates(this.currentFacility.facilityId)
      .subscribe(
        (data) => {
          this.facilityRates = data;
          if (data.length > 0) {
            this.tabs[1].icon = "md-checkmark";
          }
        },
        (error) => {}
      );
  }

  facilityRateSaveSuccess() {
    this.tabs[1].icon = "md-checkmark";
  }
}



// WEBPACK FOOTER //
// ./src/pages/company/facilities-view/create-facility/create-facility.ts